import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import {
  createColumns,
  GROUPING_RANGE_DATES,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LicensePackageBadges from "@/v2-backoffice-client/license/changes/LicensePackageBadges"
import { capabilitySheetHref } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import {
  CHANGE_BADGE_VARIANT,
  changeWordOf,
  DECISION_STATUS_DOT_VARIANT,
  displayNameOf,
  isLockedChange,
  moduleNameOf,
  packagesOf,
  PLACEMENT,
  placementOf,
  releaseOf,
  statusOf,
  whereOf,
  type LicenseChange,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"

// The Capabilities view's columns: one capability per row, in the words
// the app uses for it, what has been decided about it and which packages
// carry it. The decision itself is taken in the capability's panel (the
// row opens it), so the row stays a line an operator can scan. Split from
// the table so the board file reads as composition and this one as
// presentation.
export function createChangeColumns() {
  return createColumns<LicenseChange>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Feature: {
        id: "Feature",
        label: "Capability",
        description:
          "A part of Fusion that can be switched on or off per customer (a module, a tab, a tool), by the name the app shows, and where in the app it lives. Open it for its description, the technical trail and the decision.",
        enableHiding: false,
        defaultSize: 260,
        isWrapped: true,
        getValue: (row) =>
          `${displayNameOf(row)} ${whereOf(row)} ${row.SurfaceCode}`,
        to: ({ row }) => capabilitySheetHref(row.original.SurfaceCode),
        renderCell({ row }) {
          return (
            <span className="flex min-w-0 flex-col">
              <span className="font-medium">{displayNameOf(row.original)}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {whereOf(row.original)}
              </span>
            </span>
          )
        },
        exportValue({ row }) {
          return displayNameOf(row.original)
        },
      },
      Module: {
        id: "Module",
        label: "Part of",
        description: "The module or panel of Fusion the capability belongs to.",
        defaultSize: 130,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Module..." },
        getValue: (row) => moduleNameOf(row),
      },
      Change: {
        id: "Change",
        label: "Change",
        description:
          "New: a release added it. Removed: it is gone from the app; whatever packages included it keep the row until cleaned up. Moved: it sits somewhere else in the app now.",
        defaultSize: 100,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Change..." },
        getValue: (row) => changeWordOf(row),
        renderCell({ row }) {
          const change = row.original
          return (
            <Badge
              size="xs"
              variant={CHANGE_BADGE_VARIANT[change.ChangeKindCode] ?? "outline"}
            >
              {changeWordOf(change)}
            </Badge>
          )
        },
      },
      ChangedAt: {
        id: "ChangedAt",
        label: "Since",
        description:
          "When a deploy's scan of the app first saw the change; for capabilities that predate the feed, the day their code first appeared.",
        defaultSize: 110,
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return formatDateString(row.original.ChangedAt)
        },
      },
      Release: {
        id: "Release",
        label: "Released in",
        description:
          "The first release that shipped the capability. “Not released yet” means it is only on a branch so far.",
        defaultSize: 130,
        enableGrouping: true,
        getValue: (row) => releaseOf(row) ?? "",
        renderCell({ row }) {
          const release = releaseOf(row.original)
          if (!release) return null
          return (
            <span
              className={cn(
                !row.original.IntroducedVersion && "text-muted-foreground",
              )}
            >
              {release}
            </span>
          )
        },
      },
      // Kept under the id "Status" so the status pills' URL state and any
      // saved layout keep working; what it shows is the decision.
      Status: {
        id: "Status",
        label: "Decision",
        description:
          "Needs a decision: the capability is free for every customer and nobody has said whether that is intended. Free for everyone: kept open on purpose. Package-only: locked to the packages that include it. Removed: gone from Fusion, nothing left to decide.",
        defaultSize: 150,
        enableGrouping: true,
        getValue: (row) => statusOf(row),
        renderCell({ row }) {
          const status = statusOf(row.original)
          return (
            <DotText variant={DECISION_STATUS_DOT_VARIANT[status]}>
              {status}
            </DotText>
          )
        },
      },
      // Whether any package carries the capability, as a two-way bucket to
      // group and filter on, with the packages themselves in the cell, each
      // opening its panel. For a package-only capability they are who gets
      // it; for a free one a placement: nothing changes today, and these
      // are the packages that keep it the day it is made package-only.
      Placement: {
        id: "Placement",
        label: "Packages",
        description:
          "The packages that carry the capability, each opening its panel. For a package-only capability they are who gets it. For a free one they are a placement: it changes nothing today and says which packages keep it if it is ever made package-only. Group or filter on this to see every capability no package has placed.",
        defaultSize: 220,
        isWrapped: true,
        enableGrouping: true,
        getValue: (row) => placementOf(row),
        renderCell({ row }) {
          const change = row.original
          const isLocked = isLockedChange(change)
          return (
            <LicensePackageBadges
              names={packagesOf(change)}
              variant={isLocked ? "neutral" : "outline"}
              emptyLabel={
                isLocked && change.IsInCatalog
                  ? "Nobody yet"
                  : PLACEMENT.unplaced
              }
              isEmptyCritical={isLocked && change.IsInCatalog}
              tooltip={
                isLocked
                  ? "Customers get it through this package. Open it to see what else it carries."
                  : "Already placed here: changes nothing while it is free, and keeps it for this package's customers the day it is made package-only."
              }
            />
          )
        },
        exportValue({ row }) {
          return packagesOf(row.original).join(", ") || PLACEMENT.unplaced
        },
      },
    },
  })
}
